import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Telegram as TelegramIcon,
  ArrowRight as ArrowRightIcon,
  Key as KeyIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import config from '../config';

const Login = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [phone, setPhone] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const navigate = useNavigate();

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${config.API_BASE}/login`, {
        phone_number: phone
      });
      
      toast.success('Verification code sent!');
      localStorage.setItem('phone_number', phone);
      
      if (response.data.phone_code_hash) {
        localStorage.setItem('phone_code_hash', response.data.phone_code_hash);
      }
      
      navigate('/verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenLogin = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please enter your API token');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE}/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        onLogin(token);
        toast.success('Login successful with token!');
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Invalid or expired token');
      } else {
        toast.error('Connection failed. Please check your token and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (activeTab === 0) {
      handlePhoneLogin(e);
    } else {
      handleTokenLogin(e);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ width: '100%', borderRadius: 3, bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <TelegramIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {config.APP_NAME}
              </Typography>
              <Typography color="textSecondary">
                Connect to your Telegram account
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} centered>
                <Tab 
                  icon={<PhoneIcon />} 
                  label="Phone Login" 
                  sx={{ minWidth: '50%' }}
                />
                <Tab 
                  icon={<KeyIcon />} 
                  label="Token Login" 
                  sx={{ minWidth: '50%' }}
                />
              </Tabs>
            </Box>

            {activeTab === 0 ? (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Enter your phone number with country code (e.g., +855967171785)
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                Use your existing API token. You can get this from a previous login.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {activeTab === 0 ? (
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+855967171785"
                  sx={{ mb: 3 }}
                  disabled={loading}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="API Token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your JWT token"
                  sx={{ mb: 3 }}
                  disabled={loading}
                  required
                  type={showToken ? 'text' : 'password'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <KeyIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowToken(!showToken)}
                          edge="end"
                        >
                          {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Button
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>
                    {activeTab === 0 ? 'Send Code' : 'Login with Token'}
                    <ArrowRightIcon sx={{ ml: 1 }} />
                  </>
                )}
              </Button>
            </form>

            {activeTab === 1 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Where to find your token:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  1. After phone login, the token is stored in your browser
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  2. Check browser's Local Storage for "telegram_token"
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  3. Or get it from the API verify endpoint response
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                By continuing, you agree to our Terms of Service
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;